import React from 'react';
import { useContext, useState, useEffect, useRef } from 'react';
import 'katex/dist/katex.min.css';
import { useDebounce } from '../../hooks/useDebounce';
import ImageZoom from '../ImageZoom/ImageZoom';
import './Question.css';
import { Form, Spinner, Overlay } from 'react-bootstrap';
import dotsIcon from '../../assets/icons/tripledots.svg';
import { SimulationContext } from '../../context/SimulationContext';
import uncertain_icon from '../../assets/icons/uncertain_icon.svg';
import error_review_img from '../../assets/icons/error-review.svg';
import error_uncertain_review_img from '../../assets/icons/uncertain_wrong.svg';
import uncertain_review_icon from '../../assets/icons/correct_uncertain.svg';

import { useTranslation } from 'react-i18next';
import { prefixedT } from '../../i18n/utils';

import Latex from 'react-latex-next';

export default function FillTextQuestion(questionProps) {
  const { simulationData } = useContext(SimulationContext);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [userAnswer, setUserAnswer] = useState('');
  const [isUncertain, setIsUncertain] = useState(false);
  // const [showHints, setShowHints] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [previousQuestionNumber, setPreviousQuestionNumber] = useState(null);
  const [lastSubmittedAnswer, setLastSubmittedAnswer] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [showDeleteTooltip, setShowDeleteTooltip] = useState(false);
  const tooltipTimeoutRef = useRef(null);
  const target = useRef();
  const inputRef = useRef();
  const labelRef = useRef();

  const { t } = useTranslation();

  const debouncedUserAnswer = useDebounce(userAnswer, 500);

  const getTooltipCount = () => {
    try {
      const count = localStorage.getItem('fillTextTooltipCount');
      return count ? parseInt(count, 10) : 0;
    } catch (error) {
      console.warn('Error accessing localStorage for tooltip count:', error);
      return 0;
    }
  };

  const saveTooltipCount = (count) => {
    try {
      localStorage.setItem('fillTextTooltipCount', count.toString());
    } catch (error) {
      console.warn('Error saving tooltip count to localStorage:', error);
    }
  };

  const getFirstTooltipQuestionId = () => {
    try {
      return localStorage.getItem('fillTextFirstTooltipQuestionId');
    } catch (error) {
      console.warn(
        'Error accessing localStorage for first tooltip question ID:',
        error
      );
      return null;
    }
  };

  const saveFirstTooltipQuestionId = (questionId) => {
    try {
      localStorage.setItem(
        'fillTextFirstTooltipQuestionId',
        questionId.toString()
      );
    } catch (error) {
      console.warn(
        'Error saving first tooltip question ID to localStorage:',
        error
      );
    }
  };

  const canShowTooltip = () => {
    return getTooltipCount() < 2;
  };

  const handleInputChange = (e) => {
    try {
      const value = e?.target?.value || '';
      if (value.length <= 16) {
        setUserAnswer(value);

        if (!value.trim() && isUncertain) {
          setIsUncertain(false);
        }
      }
      if (e?.target) {
        e.target.scrollLeft = 0;
      }
    } catch (error) {
      console.warn('Error in handleInputChange:', error);
    }
  };

  const handleKeyDown = (e) => {
    try {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      if (
        (e.key === 'Backspace' || e.key === 'Delete') &&
        userAnswer.length > 0 &&
        canShowTooltip()
      ) {
        const currentQuestionId = questionProps.data?.question_number;
        if (!currentQuestionId) return;

        const storedFirstQuestionId = getFirstTooltipQuestionId();
        const tooltipCount = getTooltipCount();

        if (tooltipCount === 0) {
          setShowDeleteTooltip(true);

          saveTooltipCount(1);
          saveFirstTooltipQuestionId(currentQuestionId);

          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
          }

          tooltipTimeoutRef.current = setTimeout(() => {
            setShowDeleteTooltip(false);
            tooltipTimeoutRef.current = null;
          }, 3000);
        } else if (
          tooltipCount === 1 &&
          storedFirstQuestionId &&
          currentQuestionId.toString() !== storedFirstQuestionId
        ) {
          setShowDeleteTooltip(true);

          saveTooltipCount(2);

          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
          }

          tooltipTimeoutRef.current = setTimeout(() => {
            setShowDeleteTooltip(false);
            tooltipTimeoutRef.current = null;
          }, 3000);
        }
      }
    } catch (error) {
      console.warn('Error in handleKeyDown:', error);
    }
  };

  const handleSlotClick = () => {
    try {
      if (inputRef.current) {
        inputRef?.current?.focus();
        setTimeout(() => {
          try {
            if (inputRef.current) {
              const length = inputRef?.current?.value?.length;
              inputRef?.current?.setSelectionRange(length, length);
            }
          } catch (error) {
            console.warn('setSelectionRange not supported:', error);
          }
        }, 0);
      }
    } catch (error) {
      console.warn('Error in handleSlotClick:', error);
    }
  };

  const handleInputFocus = () => {
    try {
      if (inputRef.current) {
        setTimeout(() => {
          try {
            if (inputRef.current) {
              const length = inputRef?.current?.value?.length;
              inputRef?.current?.setSelectionRange(length, length);
            }
          } catch (error) {
            console.warn('setSelectionRange not supported:', error);
          }
        }, 0);
      }
    } catch (error) {
      console.warn('Error in handleInputFocus:', error);
    }
  };

  const handleInputClick = (e) => {
    try {
      if (inputRef.current) {
        setTimeout(() => {
          try {
            if (inputRef.current) {
              const length = inputRef?.current?.value?.length;
              inputRef?.current?.setSelectionRange(length, length);
            }
          } catch (error) {
            console.warn('setSelectionRange not supported:', error);
          }
        }, 0);
      }
    } catch (error) {
      console.warn('Error in handleInputClick:', error);
    }
  };

  useEffect(() => {
    try {
      if (!isInitialized) {
        return;
      }

      const currentAnswer = questionProps.data?.selected_answer;
      const trimmedAnswer = debouncedUserAnswer.trim();
      const currentQuestionNumber = questionProps.data?.question_number;

      if (!questionProps.data || currentQuestionNumber === null || currentQuestionNumber === undefined) {
        return;
      }

      if (
        previousQuestionNumber !== null &&
        previousQuestionNumber !== currentQuestionNumber
      ) {
        return;
      }

      if (trimmedAnswer === lastSubmittedAnswer) {
        return;
      }

      if (trimmedAnswer !== userAnswer.trim()) {
        return;
      }

      if (
        trimmedAnswer === (currentAnswer || '') ||
        (!trimmedAnswer && !currentAnswer)
      ) {
        return;
      }

      setLastSubmittedAnswer(trimmedAnswer);
      if (questionProps?.handleAnswerQuestion) {
        questionProps.handleAnswerQuestion(trimmedAnswer || null, isUncertain);
      }
    } catch (error) {
      console.warn('Error in useEffect (answer submission):', error);
    }
  }, [
    debouncedUserAnswer,
    isUncertain,
    isInitialized,
    questionProps.data?.question_number,
    previousQuestionNumber,
    lastSubmittedAnswer,
    userAnswer,
    questionProps,
  ]);

  const resetAnswer = () => {
    try {
      setUserAnswer('');
      setIsUncertain(false);
      setLastSubmittedAnswer('');
      if (questionProps?.handleAnswerQuestion) {
        questionProps.handleAnswerQuestion(null, false);
      }
    } catch (error) {
      console.warn('Error in resetAnswer:', error);
    }
  };

  const toggleUncertainty = () => {
    try {
      const newUncertaintyState = !isUncertain;
      setIsUncertain(newUncertaintyState);

      if (userAnswer.trim() && questionProps?.handleAnswerQuestion) {
        questionProps.handleAnswerQuestion(
          userAnswer.trim(),
          newUncertaintyState
        );
      }
    } catch (error) {
      console.warn('Error in toggleUncertainty:', error);
    }
  };

  // const handleSelectHint = (answerNumber) => {
  //   questionProps.handleAnswerQuestion(answerNumber, true);
  // };

  const formatText = (original_text = '') => {
    try {
      return original_text
        ?.replaceAll('<p', '< p')
        ?.replaceAll('\n', '<br />')
        ?.replaceAll('$$', '$');
    } catch (error) {
      console.warn('Error in formatText:', error);
      return original_text || '';
    }
  };

  const renderQuestionText = (text) => {
    try {
      if (!text) return '';

      // eslint-disable-next-line no-useless-escape
      const blankPattern = /\[(?::blank:|\.\.\.|\_{3,})\]/i;
      const parts = text.split(blankPattern);

      if (parts.length <= 1) {
        return (
          <div className='question-text-content'>
            <Latex>{formatText(text)}</Latex>
          </div>
        );
      }

      return (
        <div className='question-text-inline'>
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              {part && part.trim() && (
                <span className='question-part-inline'>
                  <Latex>{formatText(part)}</Latex>
                </span>
              )}
              {index < parts.length - 1 && (
                <img
                  src={dotsIcon}
                  alt='[blank]'
                  className='question-mark-icon-inline'
                />
              )}
            </React.Fragment>
          ))}
        </div>
      );
    } catch (error) {
      console.warn('Error in renderQuestionText:', error);
      return <div className='question-text-content'>{text || ''}</div>;
    }
  };

  useEffect(() => {
    try {
      const currentQuestionNumber = questionProps.data?.question_number;
      const isQuestionChange = previousQuestionNumber !== currentQuestionNumber;
      
      if (isQuestionChange) {
        setIsInitialized(false);
        setUserAnswer('');
        setIsUncertain(false);
        // setShowHints(false);
        setLastSubmittedAnswer('');
        setShowDeleteTooltip(false);

        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
          tooltipTimeoutRef.current = null;
        }

        if (
          questionProps.data?.selected_answer !== null &&
          questionProps.data?.selected_answer !== undefined
        ) {
          if (typeof questionProps.data.selected_answer === 'string') {
            setUserAnswer(questionProps.data.selected_answer);
            setLastSubmittedAnswer(questionProps.data.selected_answer);
          } else if (!isNaN(questionProps.data.selected_answer)) {
            const selectedAnswerObj = questionProps.data?.answers?.find(
              (a) => a.answer_number === questionProps.data.selected_answer
            );
            if (selectedAnswerObj) {
              setUserAnswer(selectedAnswerObj.text || '');
              // setShowHints(true);
              setLastSubmittedAnswer(selectedAnswerObj.text || '');
            }
          }
          setIsUncertain(!!questionProps.data.is_uncertain);
        }

        setPreviousQuestionNumber(currentQuestionNumber);

        setTimeout(() => {
          setIsInitialized(true);
        }, 50);
      } else if (isInitialized) {
        const serverAnswer = questionProps.data?.selected_answer;
        const serverUncertain = !!questionProps.data?.is_uncertain;
        
        const serverAnswerStr = serverAnswer === null || serverAnswer === undefined ? '' : String(serverAnswer);
        const lastSubmittedStr = lastSubmittedAnswer === null || lastSubmittedAnswer === undefined ? '' : String(lastSubmittedAnswer);
        
        
        if (serverAnswerStr !== lastSubmittedStr || serverUncertain !== isUncertain) {
          const isInputFocused = document.activeElement === inputRef.current;
          const hasUserInput = userAnswer.trim().length > 0;
          
          if (!isInputFocused || !hasUserInput) {
            if (typeof serverAnswer === 'string' || serverAnswer === null || serverAnswer === undefined) {
              const newValue = serverAnswer || '';
              setUserAnswer(newValue);
              setLastSubmittedAnswer(newValue);
            }
            setIsUncertain(serverUncertain);
          } 
        }
      } 
    } catch (error) {
      console.warn('Error in useEffect (question initialization):', error);
      if (previousQuestionNumber !== questionProps.data?.question_number) {
        setIsInitialized(true);
      }
    }
  }, [questionProps.data, questionProps.currentQuestion, questionProps.data?.selected_answer, questionProps.data?.is_uncertain]);

  useEffect(() => {
    try {
      if (!isInitialized || !questionProps.data) {
        return;
      }

      const currentQuestionNumber = questionProps.data?.question_number;
      if (previousQuestionNumber !== currentQuestionNumber) {
        return;
      }

      const serverAnswer = questionProps.data?.selected_answer;
      const serverUncertain = !!questionProps.data?.is_uncertain;
      
      const serverAnswerStr = serverAnswer === null || serverAnswer === undefined ? '' : String(serverAnswer);
      const lastSubmittedStr = lastSubmittedAnswer === null || lastSubmittedAnswer === undefined ? '' : String(lastSubmittedAnswer);
      
      
      if (serverAnswerStr !== lastSubmittedStr || serverUncertain !== isUncertain) {
        
        const isInputFocused = document.activeElement === inputRef.current;
        const hasUserInput = userAnswer.trim().length > 0;
        const userAnswerMatchesServer = userAnswer.trim() === serverAnswerStr;
        
        if (!userAnswerMatchesServer) {
          const newValue = serverAnswer || '';
          setUserAnswer(newValue);
          setLastSubmittedAnswer(newValue);
          setIsUncertain(serverUncertain);
        } else {
          if (serverUncertain !== isUncertain) {
            setIsUncertain(serverUncertain);
          }
          if (lastSubmittedStr !== serverAnswerStr) {
            setLastSubmittedAnswer(serverAnswerStr);
          }
        }
      }
    } catch (error) {
      console.warn('Error in external answer update useEffect:', error);
    }
  }, [questionProps.data?.selected_answer, questionProps.data?.is_uncertain]);

  useEffect(() => {
    setImageLoaded(false);
  }, [questionProps?.data?.image_url]);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  if (!questionProps) {
    console.warn('FillTextQuestion: questionProps is missing');
    return (
      <div className='col-9 exam_question_container px-4 pb-5'>Loading...</div>
    );
  }

  return (
    <div className={`col-9 exam_question_container px-4 pb-5`}>
      {questionProps?.data && (
        <div className='fill-text-question'>
          {/* Header */}
          <div className='d-flex justify-content-between align-items-center'>
            {simulationData?.type === 'incorrect_questions' &&
            simulationData?.tag !== 'self_evaluation' ? (
              <div className='d-flex align-items-center mb-2'>
                {questionProps.data.incorrect_question && (
                  <>
                    {questionProps.data.was_incorrect &&
                      !questionProps.data.was_uncertain && (
                        <img
                          src={error_review_img}
                          alt='Incorrect'
                          className='me-2 pointer'
                          ref={target}
                          onClick={() => setShowTooltip(!showTooltip)}
                        />
                      )}
                    {questionProps.data.was_incorrect &&
                      questionProps.data.was_uncertain && (
                        <img
                          src={error_uncertain_review_img}
                          alt='Incorrect but uncertain'
                          className='me-2 pointer'
                          ref={target}
                          onClick={() => setShowTooltip(!showTooltip)}
                        />
                      )}
                    {!questionProps.data.was_incorrect &&
                      questionProps.data.was_uncertain && (
                        <img
                          src={uncertain_review_icon}
                          alt='Correct but uncertain'
                          className='me-2 pointer'
                          ref={target}
                          onClick={() => setShowTooltip(!showTooltip)}
                        />
                      )}
                    <Overlay
                      target={target.current}
                      show={showTooltip}
                      rootClose={true}
                      onHide={() => {
                        setShowTooltip(false);
                      }}
                      rootCloseEvent='click'
                      placement='bottom'
                      className={
                        simulationData?.is_bocconi_design
                          ? 'bocconi'
                          : 'center_modal'
                      }
                    >
                      {({
                        placement,
                        arrowprops,
                        show: _show,
                        popper,
                        ...propsOverlay
                      }) => (
                        <div
                          {...propsOverlay}
                          style={{
                            position: 'absolute',
                            backgroundColor: '#fff',
                            padding: '16px 20px',
                            color: 'rgba(55, 55, 55, 0.75)',
                            fontSize: 16,
                            borderRadius: 16,
                            maxWidth: '250px',
                            height: 'max-content',
                            boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.25)',
                            transform: 'translateY(15px)',
                            ...propsOverlay.style,
                          }}
                          className={`tooltip_arrow_top ${
                            simulationData?.is_bocconi_design && 'bocconi'
                          }`}
                        >
                          {questionProps.data.was_incorrect &&
                            !questionProps.data.was_uncertain && (
                              <>
                                {t(prefixedT('QUESTION.PAST_RESULT'))}
                                <img
                                  src={error_review_img}
                                  alt='incorrect'
                                  className=''
                                  style={{ maxWidth: '20px' }}
                                />{' '}
                                <strong className='text-tf'>
                                  {t(prefixedT('QUESTION.ERROR'))}
                                </strong>
                                {t(prefixedT('QUESTION.PAST_RESULT_END'))}
                              </>
                            )}
                          {questionProps.data.was_incorrect &&
                            questionProps.data.was_uncertain && (
                              <>
                                {t(prefixedT('QUESTION.PAST_ANSWER'))}
                                <img
                                  src={error_review_img}
                                  alt='incorrect'
                                  className=''
                                  style={{ maxWidth: '20px' }}
                                />{' '}
                                <strong className='text-tf'>
                                  {t(prefixedT('QUESTION.WRONG_ANSWER'))}
                                </strong>
                                {'e '}
                                <img
                                  src={uncertain_icon}
                                  alt='incorrect'
                                  style={{ maxWidth: '20px' }}
                                  className=''
                                />
                                <strong className='text-tf'>
                                  {t(prefixedT('QUESTION.UNSURE_ANSWER'))}
                                </strong>
                                {t(prefixedT('QUESTION.PAST_ANSWER_END'))}
                              </>
                            )}
                          {!questionProps.data.was_incorrect &&
                            questionProps.data.was_uncertain && (
                              <>
                                {t(prefixedT('QUESTION.PAST_ANSWER_CORRECT'))}
                                <img
                                  src={uncertain_icon}
                                  alt='incorrect'
                                  style={{ maxWidth: '16px' }}
                                  className=''
                                />
                                <strong className='text-tf'>
                                  {t(prefixedT('QUESTION.UNSURE_ANSWER'))}
                                </strong>
                                {t(prefixedT('QUESTION.PAST_ANSWER_END'))}
                              </>
                            )}
                        </div>
                      )}
                    </Overlay>
                  </>
                )}
                <p className='mb-0'>
                  {`${t(prefixedT('QUESTION.QUESTION'))} ${
                    questionProps.data.virtual_question_number
                  } `}
                  {questionProps.data.subject_name && (
                    <span className='text-secondary'>
                      {`- (${questionProps.data.subject_name})`}
                    </span>
                  )}
                </p>
              </div>
            ) : (
              <div className='fill-text-header py-1'>
                <h5>{t(prefixedT('QUESTION.COMPLETE_THE_SENTENCE'))}</h5>
              </div>
            )}
            {questionProps.data.is_sending_answer && (
              <Spinner animation='border' role='status' />
            )}
          </div>

          {/* Question Text */}
          <div className='mt-2'>
            {renderQuestionText(questionProps.data?.question)}
          </div>

          {/* Image Preview */}
          {questionProps.data.image_url && (
            <>
              <div className='image_preview mt-4'>
                <ImageZoom>
                  <img
                    src={questionProps.data.image_url}
                    alt='Question'
                    style={{ visibility: imageLoaded ? 'visible' : 'hidden' }}
                    className='w-100'
                    onLoad={() => {
                      setImageLoaded(true);
                    }}
                  />
                </ImageZoom>
                <small className='d-block text-secondary'>
                  {t(prefixedT('QUESTION.IMAGE_ZOOM'))}
                </small>
                {!imageLoaded && (
                  <div className='lds-ring'>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                )}
              </div>
            </>
          )}

          <div className='fill-text-answer-section mt-2'>
            {/* <div className='d-flex mt-3 align-items-center justify-content-end'>
              <span
                className={`reset_answer ${
                  userAnswer.trim() ? '' : 'opacity-0'
                }`}
                style={userAnswer.trim() ? {} : { cursor: 'auto' }}
                onClick={userAnswer.trim() ? resetAnswer : null}
              >
                {t(prefixedT('QUESTION.RESET_ANSWER'))}
              </span>
            </div>{' '} */}
            <Form onSubmit={(e) => e.preventDefault()}>
              <Form.Group>
                <div style={{ position: 'relative' }}>
                  <Form.Label
                    className='fill-text-form-label'
                    style={{ paddingTop: '18px' }}
                    ref={labelRef}
                  >
                    {t(prefixedT('QUESTION.YOUR_ANSWER'))}{' '}
                    <span className='text-muted' style={{ fontSize: '0.75em' }}>
                      {t(prefixedT('QUESTION.MAX_CHARACTERS'))}
                    </span>
                  </Form.Label>
                  <Overlay
                    target={labelRef.current}
                    show={showDeleteTooltip}
                    placement='top'
                    className={
                      simulationData?.is_bocconi_design
                        ? 'bocconi'
                        : 'center_modal'
                    }
                  >
                    {({
                      placement,
                      arrowprops,
                      show: _show,
                      popper,
                      ...propsOverlay
                    }) => (
                      <div
                        {...propsOverlay}
                        style={{
                          position: 'absolute',
                          backgroundColor: '#fff',
                          padding: '12px 16px',
                          color: 'rgba(55, 55, 55, 0.85)',
                          fontSize: 14,
                          borderRadius: 8,
                          maxWidth: '300px',
                          height: 'max-content',
                          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                          zIndex: 1000,
                          transform: 'translateY(-25px) translateX(-120px)',
                          ...propsOverlay.style,
                        }}
                        className={`tooltip_arrow_bottom ${
                          simulationData?.is_bocconi_design && 'bocconi'
                        }`}
                      >
                        <h3
                          style={{
                            fontFamily: 'Nunito',
                            fontWeight: 600,
                            fontSize: '16px',
                            lineHeight: '130%',
                            letterSpacing: '0%',
                            margin: 0,
                            color: 'inherit',
                          }}
                        >
                          {t(prefixedT('QUESTION.DELETE_WARNING'))}
                        </h3>
                      </div>
                    )}
                  </Overlay>
                </div>
                <div className='fill-text-input-wrapper'>
                  <div
                    className='fill-text-input-16-char-wrapper'
                    onClick={handleSlotClick}
                  >
                    <Form.Control
                      as='input'
                      ref={inputRef}
                      value={userAnswer}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      onFocus={handleInputFocus}
                      onClick={handleInputClick}
                      maxLength={16}
                      className={`fill-text-input-16-char ${
                        isUncertain ? 'is_uncertain_input' : ''
                      }`}
                    />
                    <div className='fill-text-visual-slots'>
                      {Array.from({ length: 16 }, (_, index) => (
                        <div
                          key={index}
                          className={`fill-text-slot ${
                            index === userAnswer.length ? 'active' : ''
                          } ${
                            index === userAnswer.length && isUncertain
                              ? 'uncertain'
                              : ''
                          }`}
                        >
                          {userAnswer[index] || ''}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Form.Group>
            </Form>
            <div className='d-flex align-items-center gap-2 mt-4 uncertainty-section'>
              {simulationData.type === 'incorrect_questions' &&
                simulationData.tag !== 'self_evaluation' && (
                  <div className='d-flex align-items-center'>
                    <Form.Check
                      type='checkbox'
                      id='uncertain-checkbox'
                      checked={isUncertain}
                      onChange={toggleUncertainty}
                      disabled={!userAnswer.trim()}
                      className={`me-2 uncertainty-checkbox ${
                        isUncertain ? 'checked' : ''
                      }`}
                    />
                    <span>Segna come</span>
                    <img
                      src={uncertain_icon}
                      alt='Uncertain'
                      className='mx-1'
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span>non sicura</span>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
